/**
 * @file 国际化入口文件
 * @date 2021-06-24
 */
import { ls } from '@sfe/bone-cache';
import { createI18n } from 'vue-i18n';
import {
    locale as DEFAULT_LOCALE,
    LOCALE_LS_KEY,
    RTL_LOCALES,
    locales,
} from '@/config/site';

export const i18n = createI18n({
    legacy: false,
    globalInjection: true, // 全局注入 $t 函数
    locale: (() => {
        const currLocale = ls.get(LOCALE_LS_KEY) || DEFAULT_LOCALE;
        let realLocale = currLocale;
        switch (currLocale) {
            case 'zh-CN':
                realLocale = 'zh-MY';
                break;

            case 'fr-FR':
                realLocale = DEFAULT_LOCALE;
                break;

            default:
                break;
        }
        return realLocale;
    })(),
    silentFallbackWarn: true,
    silentTranslationWarn: true,
});

/**
 * 已经异步加载完成的语言包名称
 */
const loadedLanguages = [];

/**
 * 获取浏览器支持语言.
 *
 * @return {string|undefined}
 */
export const getBrowserLanguage = () => navigator.language || navigator.browserLanguage || navigator.userLanguage;

/**
 * 获取浏览器缓存中的语言
 *
 * @return {string}
 */
export const getCookieLanguage = () => {
    const value = ls.get(LOCALE_LS_KEY);
    return value;
};

/**
 * 判断语言是否支持，大小写敏感
 *
 * @param {any} locale - 语言
 * @return {boolean}
 */
export const isSupport = (locale) => locale && locales.includes(locale);

/**
 * 替换链接中的语言标识
 *
 * @description
 * 1. 会自动替换错误的 //
 * 2. 根据当前的语言，自动处理前缀
 * 3. 如果为单语言，将自动删除 lang 前缀
 *
 * @param  {string} path - 路径链接.
 * @param  {string} [lang=i18n.locale] - 追加语言，默认为当前语言
 * @return {string}
 *
 * @example
 *  1. 默认追加当前语言
 *      replace('/login') => /zh-MY/login
 *
 *  2. 替换当前语言
 *      replace('/en/login') => /zh-MY/login
 *
 *  3. 替换参数语言
 *      replace('/en/login', 'zh') => /zh/login
 *  4. 自动修改链接中的 /
 *      replace('//zh-MY///login') => /zh-MY/login
 */
export const replace = (path = '', lang = i18n.global.locale.value) => {
    const paths = path
        .replace(/^[\\/]+/, '') // 过滤开头的 / ，用来做路径分隔。过滤转义符，防止外链接钓鱼
        .split(/\/+/); // 以 / 分隔来取 lang

    // 如果一级是语言，则删除
    if (isSupport(paths[0])) {
        paths.splice(0, 1);
    }

    // 处理多语言前缀
    return lang ? `/${lang}/${paths.join('/')}` : `/${paths.join('/')}`;
};

/**
 * 查找修正的语言
 *
 * @param {string} lang 目标语言
 * @return {(string|null)} 修正之后的语言
 */
export const find = (lang = '') => {
    const locale = String(lang).toLowerCase();
    let result = null;

    locales.some((key) => {
        // 如果目标完全一致
        if (key.toLowerCase() === locale) {
            result = key;
            return true;
        }
        return false;
    });

    return result;
};

/**
 * 获取完整路径
 *
 * @param {string} [locale=i18n.locale] 目录语言
 * @return {string}
 */
export const getFullPath = (locale = i18n.locale) => {
    const pathname = window.location.pathname.replace(/[&<>"']/g, '');
    const url = `${pathname}${window.location.search}${window.location.hash}`;
    return replace(url, locale);
};

/**
 * 异步加载语言包
 *
 * @param {string} locale 目录语言
 * @return {Promise}
 */
export const setAsyncLocale = (locale = '') => {
    ls.set(LOCALE_LS_KEY, locale);

    if (!loadedLanguages.includes(locale)) {
        return import(/* webpackChunkName: "locale-[request]" */ `@/locales/${locale}/web-spot-launchpad.js`).then((res) => {
            // eslint-disable-next-line no-param-reassign
            if (res.default) res = res.default;
            loadedLanguages.push(locale);
            i18n.global.setLocaleMessage(locale, res);
            // 优先设置语言包，让其他地方可以使用
            i18n.global.locale.value = locale;
        });
    }

    // 优先设置语言包，让其他地方可以使用
    i18n.global.locale.value = locale;
    return Promise.resolve();
};

/**
 * 重定向到语言链接
 *
 * @param {string} locale 目标语言
 */
export const redirectTo = (locale) => {
    ls.set(LOCALE_LS_KEY, locale);

    // 跳转链接  getFullPath方法中已处理xss安全问题，安全扫描可略过
    window.location.href = getFullPath(locale);
};

/**
 * 设置语言
 *
 * @param {Object} params 配置参数
 * @param {string} params.locale 目标语言
 * @param {boolean} [params.redirect=true] 是否跳转
 *
 * @example
 *  1. 直接设置语言，默认链接自动跳转
 *  setLocale({ locale: 'zh-MY' });
 *
 *  2. 设置不跳转链接链接
 *  setLocale({
 *      locale: 'zh-MY',
 *      redirect: false,
 *  });
 */
export const setLocale = ({ locale = '', redirect = false } = {}) => {
    // 如果目标语言不支持，则忽略
    if (!isSupport(locale)) {
        return;
    }

    if (redirect) {
        redirectTo(locale);
    } else {
        // 动态更新链接
        window.history.pushState(null, '', getFullPath(locale));

        setAsyncLocale(locale);
    }
};

/**
 * 浏览器跳转到登录页
 * 1. 如果当前不是登录页，则携带当前页面路径、参数、hash 跳转到 `/login/`
 * 2. 如果当前已经是登录页，则没有反应
 */
export function toLogin() {
    const { location } = window;
    if (replace(location.pathname, null).indexOf('/login') === 0) return; // 如果已经是在登录页
    const url = replace('/login/');
    window.location.assign(url);
}

/**
 * 重置HTML标签的dir属性
 *
 * @param {*} lang 语言
 */
export const resetHtmlDir = (lang) => {
    if (RTL_LOCALES.includes(lang)) {
        document.documentElement.setAttribute('dir', 'rtl');
    } else {
        document.documentElement.setAttribute('dir', 'ltr');
    }
};
